import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="safe-area-screen flex flex-col bg-background overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(safe-area-inset-bottom),2rem)]">
          <div className="flex items-center gap-2 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Privacy & Terms of Service</h1>
            </div>
          </div>

          <Card className="p-6 space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">Terms of Service</h2>
              <p className="text-muted-foreground mb-4">
                Last updated: {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm leading-relaxed mb-4">
                By using Global Chat Connect, you agree to these terms. Please read them carefully.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">1. Translation Disclaimer</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>IMPORTANT:</strong> Global Chat Connect provides automated translation services to facilitate communication across languages. However, we make no warranties or guarantees regarding:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The accuracy, completeness, or reliability of any translations</li>
                  <li>The appropriateness of translations for any particular use or context</li>
                  <li>The preservation of nuance, tone, or cultural context in translations</li>
                </ul>
                <p className="font-semibold">
                  You acknowledge that automated translations may contain errors, inaccuracies, or misinterpretations. Global Chat Connect is not responsible for any consequences arising from reliance on translated content.
                </p>
                <p>
                  For critical communications, professional human translation services are recommended.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">2. User-Generated Content Disclaimer</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  Global Chat Connect is a communication platform that enables users to exchange messages and content. We are <strong>not responsible</strong> for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The content, accuracy, or legality of any messages or files shared by users</li>
                  <li>Any offensive, inappropriate, illegal, or harmful content shared between users</li>
                  <li>Any agreements, disputes, or relationships formed through the platform</li>
                  <li>Any damages or losses resulting from user interactions or shared content</li>
                </ul>
                <p className="font-semibold">
                  Users are solely responsible for their own content and interactions. You agree to use Global Chat Connect in compliance with all applicable laws and regulations.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">3. User Responsibilities</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>As a user of Global Chat Connect, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate registration information</li>
                  <li>Keep your account credentials secure</li>
                  <li>Not share illegal, harmful, or inappropriate content</li>
                  <li>Not use the service to harass, threaten, or harm others</li>
                  <li>Not attempt to circumvent security measures or disrupt the service</li>
                  <li>Respect the privacy and intellectual property rights of others</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">4. Privacy Policy</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  <strong>Data Collection:</strong> We collect and process the following information:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account information (name, email, phone number)</li>
                  <li>Profile data (language preferences, profile picture)</li>
                  <li>Messages and content you send through the platform</li>
                  <li>Usage data and technical information</li>
                </ul>
                <p>
                  <strong>Data Use:</strong> Your data is used to provide and improve our services, including message translation and delivery.
                </p>
                <p>
                  <strong>Data Storage:</strong> Your data is securely stored and encrypted. We do not sell your personal information to third parties.
                </p>
                <p>
                  <strong>Data Sharing:</strong> Messages are shared only with intended recipients. We may use third-party services (translation APIs, payment processors) that have access to certain data as necessary to provide services.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">5. Service Availability</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  Global Chat Connect is provided "as is" without warranties of any kind. We do not guarantee:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Uninterrupted or error-free service</li>
                  <li>That the service will meet your specific requirements</li>
                  <li>That all features will be available at all times</li>
                </ul>
                <p>
                  We reserve the right to modify, suspend, or discontinue any aspect of the service at any time.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">6. Limitation of Liability</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p className="font-semibold">
                  To the maximum extent permitted by law, Global Chat Connect and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Loss of profits, data, or goodwill</li>
                  <li>Service interruptions or security breaches</li>
                  <li>Errors in translation or content</li>
                  <li>User-generated content or interactions</li>
                  <li>Unauthorized access to your account</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">7. Subscription and Payments</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  Global Chat Connect offers subscription-based services. By subscribing, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Pay all applicable fees as described at the time of purchase</li>
                  <li>Automatic renewal unless you cancel before the renewal date</li>
                  <li>Our refund policy as outlined in your subscription terms</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">8. Termination</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  We reserve the right to suspend or terminate your account if you violate these terms or engage in activities that harm the platform or other users.
                </p>
                <p>
                  You may delete your account at any time through the settings page.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">9. Changes to Terms</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  We may update these terms from time to time. Continued use of Global Chat Connect after changes constitutes acceptance of the updated terms.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">10. Contact</h3>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>
                  For questions about these terms or privacy concerns, please contact us through the app settings.
                </p>
              </div>
            </section>

            <div className="pt-4 border-t mt-6">
              <p className="text-xs text-muted-foreground">
                By using Global Chat Connect, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and Privacy Policy.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
